import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { UserPermissionGroupsResponse } from 'src/apis/authorization-service/types';
import type { AuthorizationServiceRequest } from 'src/apis/authorization-service/use-authorization-service';
import type { Successful } from 'src/hooks/use-axios';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookProps = {
  authorizationService?: AuthorizationServiceRequest;
  userId: string;
};

type ExecuteProps = {
  authorizationService: Successful<AuthorizationServiceRequest>;
  userId: string;
};

export function useUserPermissionGroups({
  authorizationService,
  userId,
}: HookProps) {
  const request = useAxios(
    (axios, baseConfig, { authorizationService, userId }: ExecuteProps) => {
      if (
        !authorizationService.response.data._links.manageUserAssignments
          ?.href &&
        !authorizationService.response.data._links.manageUserAssignmentsV2?.href
      )
        throw new Forbidden();

      const link =
        authorizationService.response.data._links.manageUserAssignmentsV2 ??
        authorizationService.response.data._links.manageUserAssignments!;

      const url = urlTemplate.parse(link.href).expand({ userId });

      return axios.request<UserPermissionGroupsResponse>({
        ...baseConfig,
        url,
      });
    },
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!authorizationService?.response) return;
    request.execute({ authorizationService, userId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationService?.response, userId]);

  if (!authorizationService) return;

  return request;
}

export type UserPermissionGroupsRequest = ReturnType<
  typeof useUserPermissionGroups
>;

export function useInviteeUserPermissionGroups() {
  const request = useAxios(
    (axios, baseConfig, { authorizationService, userId }: ExecuteProps) => {
      if (
        !authorizationService.response.data._links.manageUserAssignments
          ?.href &&
        !authorizationService.response.data._links.manageUserAssignmentsV2?.href
      )
        throw new Forbidden();

      const link =
        authorizationService.response.data._links.manageUserAssignmentsV2 ??
        authorizationService.response.data._links.manageUserAssignments!;

      const url = urlTemplate.parse(link.href).expand({ userId });

      return axios.request<UserPermissionGroupsResponse>({
        ...baseConfig,
        url,
      });
    },
    { neededOnPageLoad: false }
  );

  return request;
}
